import React, { useEffect, useMemo, useState } from 'react';
import { BiDownload, BiRefresh } from 'react-icons/bi';
import apiUser from '../../../apiUser';
import LoadingSpinner from '../../Loader/load';

// Pendências de cadastro (09/out/2026, só admin): por contrato ATIVO — sem número,
// sem data fim, sem destinatário PARA, cliente inativo e alerta de vigência.
// As regras moram no backend (db/pendenciasCalc.js); a tela só filtra e exporta.
export const FILTROS = [
    { chave: 'vencido', label: 'Vencidos', cls: 'border-red-300 bg-red-50 text-red-800 dark:border-red-800 dark:bg-red-950/40 dark:text-red-200' },
    { chave: 'vence_90', label: 'Vencem em até 90 dias', cls: 'border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200' },
    { chave: 'sem_data_fim', label: 'Sem data fim', cls: 'border-gray-300 bg-white text-gray-800 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100' },
    { chave: 'sem_numero', label: 'Sem número', cls: 'border-gray-300 bg-white text-gray-800 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100' },
    { chave: 'sem_para', label: 'Sem destinatário PARA', cls: 'border-gray-300 bg-white text-gray-800 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100' },
    { chave: 'cliente_inativo', label: 'Cliente inativo', cls: 'border-gray-300 bg-white text-gray-800 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100' },
];
const ROTULO = Object.fromEntries(FILTROS.map((f) => [f.chave, f.label]));
const CHIP = {
    vencido: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
    vence_90: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
};
const focoCls = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-vox-blue-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-800';
const semAcento = (v) => String(v == null ? '' : v).normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
const dataBR = (v) => (v ? `${String(v).slice(8, 10)}/${String(v).slice(5, 7)}/${String(v).slice(0, 4)}` : '—');
const brl = (v) => (v == null || v === '' ? '—' : Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }));

// Filtro ativo (todas as chaves marcadas precisam estar no contrato) + busca livre.
export const filtrarPendencias = (lista, ativos, termo) => {
    const t = semAcento(termo).trim();
    return (lista || []).filter((l) => ativos.every((k) => l.pendencias.includes(k))
        && (!t || [l.cliente_nome, l.nome, l.numero, l.estado, l.id].some((c) => semAcento(c).includes(t))));
};

const textoVigencia = (l) => {
    if (l.vigencia === 'vencido') return `venceu há ${Math.abs(l.dias_para_vencer)} dia(s)`;
    if (l.vigencia === 'vence_90') return l.dias_para_vencer === 0 ? 'vence hoje' : `vence em ${l.dias_para_vencer} dia(s)`;
    return '';
};

const Pendencias = () => {
    const [dados, setDados] = useState(null);
    const [resumo, setResumo] = useState({});
    const [hoje, setHoje] = useState('');
    const [erro, setErro] = useState('');
    const [ativos, setAtivos] = useState([]);
    const [termo, setTermo] = useState('');
    const [baixando, setBaixando] = useState(false);

    const carregar = () => {
        setDados(null);
        setErro('');
        apiUser.get('/contratos/pendencias')
            .then(({ data }) => {
                if (!data.success) { setErro(data.message || 'Não foi possível carregar as pendências.'); setDados([]); return; }
                setDados(data.dados || []);
                setResumo(data.resumo || {});
                setHoje(data.hoje || '');
            })
            .catch(() => { setErro('Sem resposta do servidor.'); setDados([]); });
    };
    useEffect(carregar, []);

    const visiveis = useMemo(() => filtrarPendencias(dados, ativos, termo), [dados, ativos, termo]);
    const alternar = (chave) => setAtivos((a) => (a.includes(chave) ? a.filter((k) => k !== chave) : [...a, chave]));

    const exportar = async () => {
        setBaixando(true);
        try {
            const resp = await apiUser.get('/contratos/pendencias', { params: { formato: 'csv' }, responseType: 'blob' });
            if (resp.data && resp.data.type === 'application/json') {
                const txt = JSON.parse(await resp.data.text());
                setErro(txt.message || 'Falha ao exportar.');
                return;
            }
            const url = window.URL.createObjectURL(resp.data);
            const a = document.createElement('a');
            a.href = url;
            a.download = `pendencias-cadastro-${hoje || 'hoje'}.csv`;
            a.click();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            setErro('Falha ao exportar o CSV.');
        } finally { setBaixando(false); }
    };

    return (
        <div className='space-y-5'>
            <header className='flex flex-wrap items-end justify-between gap-3'>
                <div>
                    <h1 className='text-2xl font-bold text-vox-blue-900 dark:text-white'>Pendências de cadastro</h1>
                    <p className='mt-1 text-sm text-gray-600 dark:text-gray-400'>
                        Contratos ativos com dado faltando para o faturamento e alerta de vigência{hoje ? ` (referência ${dataBR(hoje)})` : ''}.
                    </p>
                </div>
                <div className='flex gap-2'>
                    <button type='button' onClick={carregar}
                        className={`inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 ${focoCls}`}>
                        <BiRefresh className='h-4 w-4' aria-hidden='true' />Atualizar
                    </button>
                    <button type='button' onClick={exportar} disabled={baixando || !dados || !dados.length}
                        className={`inline-flex items-center gap-1.5 rounded-lg bg-vox-blue-900 px-3 py-2 text-sm font-semibold text-white hover:bg-vox-blue-800 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-vox-blue-600 dark:hover:bg-vox-blue-500 ${focoCls}`}>
                        <BiDownload className='h-4 w-4' aria-hidden='true' />{baixando ? 'Exportando…' : 'Exportar CSV'}
                    </button>
                </div>
            </header>

            {erro && <p role='alert' className='rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200'>{erro}</p>}

            <div role='group' aria-label='Filtrar por pendência' className='grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6'>
                {FILTROS.map((f) => {
                    const ativo = ativos.includes(f.chave);
                    return (
                        <button key={f.chave} type='button' aria-pressed={ativo} onClick={() => alternar(f.chave)}
                            className={`flex flex-col items-start rounded-lg border px-3 py-2.5 text-left transition-colors ${focoCls} ${ativo ? 'border-vox-blue-700 bg-vox-blue-900 text-white dark:bg-vox-blue-600' : f.cls}`}>
                            <span className='text-2xl font-bold tabular-nums'>{resumo[f.chave] ?? 0}</span>
                            <span className='text-xs font-medium'>{f.label}</span>
                        </button>
                    );
                })}
            </div>

            <div className='flex flex-wrap items-center justify-between gap-3'>
                <input type='search' aria-label='Buscar contrato' value={termo} onChange={(e) => setTermo(e.target.value)}
                    placeholder='Buscar por cliente, contrato, número ou UF'
                    className='w-full max-w-md rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 focus:border-vox-blue-500 focus:ring-2 focus:ring-vox-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100' />
                {dados && <span className='text-sm text-gray-600 dark:text-gray-400 tabular-nums'>{visiveis.length} de {dados.length} contrato(s)</span>}
            </div>

            {dados === null ? <LoadingSpinner /> : visiveis.length === 0 ? (
                <p className='rounded-lg border border-gray-200 bg-white p-6 text-center text-gray-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400'>
                    {dados.length ? 'Nenhum contrato com esse filtro.' : 'Nenhuma pendência nos contratos ativos.'}
                </p>
            ) : (
                <div className='overflow-x-auto rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800'>
                    <table className='min-w-full text-sm'>
                        <thead className='bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500 dark:bg-gray-900/40 dark:text-gray-400'>
                            <tr>
                                <th className='px-3 py-2'>Cliente</th>
                                <th className='px-3 py-2'>Contrato</th>
                                <th className='px-3 py-2'>Número</th>
                                <th className='px-3 py-2 text-right'>Valor</th>
                                <th className='px-3 py-2'>Data fim</th>
                                <th className='px-3 py-2'>Pendências</th>
                            </tr>
                        </thead>
                        <tbody className='divide-y divide-gray-100 dark:divide-gray-700'>
                            {visiveis.map((l) => (
                                <tr key={l.id} className='text-gray-800 dark:text-gray-100'>
                                    <td className='px-3 py-2'>{l.cliente_nome}{l.estado ? <span className='text-gray-500 dark:text-gray-400'> · {l.estado}</span> : null}</td>
                                    <td className='px-3 py-2'>{l.nome} <span className='text-xs text-gray-500 dark:text-gray-400'>#{l.id}</span></td>
                                    <td className='px-3 py-2'>{l.numero || <span className='text-gray-400'>—</span>}</td>
                                    <td className='px-3 py-2 text-right tabular-nums'>{l.tipo === 'global' ? brl(l.valor) : <span className='text-xs text-gray-500 dark:text-gray-400'>por itens</span>}</td>
                                    <td className='px-3 py-2 tabular-nums'>
                                        {dataBR(l.data_fim)}
                                        {l.vigencia && <div className={`text-xs ${l.vigencia === 'vencido' ? 'text-red-700 dark:text-red-300' : 'text-amber-700 dark:text-amber-300'}`}>{textoVigencia(l)}</div>}
                                    </td>
                                    <td className='px-3 py-2'>
                                        <div className='flex flex-wrap gap-1'>
                                            {l.pendencias.map((p) => (
                                                <span key={p} className={`whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold ${CHIP[p] || 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200'}`}>{ROTULO[p] || p}</span>
                                            ))}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default Pendencias;
